<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue';
import { useToast } from 'vue-toastification';
import { useQzStore } from '@/stores/qzStore';
import qz from 'qz-tray';
import Select from 'primevue/select';
import Accordion from 'primevue/accordion';
import AccordionPanel from 'primevue/accordionpanel';
import AccordionHeader from 'primevue/accordionheader';
import AccordionContent from 'primevue/accordioncontent';
import Checkbox from 'primevue/checkbox';

defineProps({ visible: { type: Boolean, required: true } });
const emit = defineEmits(['update:visible']);
const close = () => emit('update:visible', false);

const toast = useToast();
const qzStore = useQzStore();

const checked = ref(false);
const showPrinterConfig = ref(false);

const availableSizes = ref([]);
const availableTrays = ref([]);
const selectedSize = ref(null);
const selectedTray = ref(null);
const isLoadingPrinterSizes = ref(false);

const availableBarcodeSizes = ref([]);
const availableBarcodeTrays = ref([]);
const selectedBarcodeSize = ref(null);
const selectedBarcodeTray = ref(null);
const isLoadingBarcodePrinterSizes = ref(false);
const isPrintWithoutPriceChecked = ref(false);
const isZebraPrinterChecked = ref(false);
const zebraAutoDetected = ref(false);

const togglePrinterMode = async () => {
  if (qzStore.isConnecting) return;

  if (checked.value) {
    if (!qzStore.isQzConnected) {
      try {
        await qzStore.connectQZ(true);
        if (qzStore.isQzConnected) {
          qzStore.useQzTrayPrint = true;
          toast.success('QZ Tray connected and enabled for printing');
        }
      } catch {
        toast.error('Failed to connect QZ Tray. Please ensure QZ Tray is running.');
        checked.value = false;
      }
    } else {
      qzStore.useQzTrayPrint = true;
    }
    localStorage.setItem('useQzTrayPrint', 'true');
  } else {
    if (qzStore.isQzConnected) {
      try {
        await qzStore.disconnectQZ();
        toast.info('QZ Tray disconnected. Using Windows print only.');
      } catch {
        toast.error('Failed to disconnect QZ Tray.');
      }
    }
    qzStore.useQzTrayPrint = false;
    localStorage.setItem('useQzTrayPrint', 'false');
  }
};

function togglePrintWithoutPrice() {
  qzStore.printWithoutPrice = isPrintWithoutPriceChecked.value;
}

function toggleZebraPrinter() {
  qzStore.isZebraPrinter = isZebraPrinterChecked.value;
  zebraAutoDetected.value = false;
}

watch(() => qzStore.useQzTrayPrint, (val) => { checked.value = val; });

watch(() => qzStore.isQzConnected, (isConnected) => {
  if (!isConnected) {
    qzStore.useQzTrayPrint = false;
    checked.value = false;
  } else {
    if (localStorage.getItem('useQzTrayPrint') === 'true') {
      qzStore.useQzTrayPrint = true;
      checked.value = true;
    }
  }
});

watch(
  () => qzStore.selectedPrinter,
  async (newPrinter) => {
    if (!newPrinter?.value || !qzStore.isQzConnected) {
      availableSizes.value = [];
      availableTrays.value = [];
      return;
    }
    try {
      isLoadingPrinterSizes.value = true;
      await qzStore.getPrinterDetails();
      const details = qzStore.printerDetails;
      availableSizes.value = details?.sizes?.map(s => ({ label: s.name, value: s.name })) || [];
      availableTrays.value = (details?.trays || []).map(t =>
        typeof t === 'string' ? { label: t, value: t } : { label: t.name, value: t.name }
      );
      selectedSize.value = qzStore.selectedSizeConfig && availableSizes.value.some(s => s.value === qzStore.selectedSizeConfig.value)
        ? qzStore.selectedSizeConfig : availableSizes.value[0] ?? null;
      selectedTray.value = qzStore.selectedTrayConfig && availableTrays.value.some(t => t.value === qzStore.selectedTrayConfig.value)
        ? qzStore.selectedTrayConfig : availableTrays.value[0] ?? null;
    } catch {
      availableSizes.value = [];
      availableTrays.value = [];
    } finally {
      isLoadingPrinterSizes.value = false;
    }
  },
  { immediate: true }
);

watch(
  () => qzStore.selectedBarcodePrinter,
  async (newPrinter) => {
    if (!newPrinter?.value || !qzStore.isQzConnected) {
      availableBarcodeSizes.value = [];
      availableBarcodeTrays.value = [];
      return;
    }
    try {
      isLoadingBarcodePrinterSizes.value = true;
      await qzStore.getBarcodePrinterDetails();
      const details = qzStore.barcodePrinterDetails;
      availableBarcodeSizes.value = details?.sizes?.map(s => ({ label: s.name, value: s.name })) || [];
      availableBarcodeTrays.value = (details?.trays || []).map(t =>
        typeof t === 'string' ? { label: t, value: t } : { label: t.name, value: t.name }
      );
      selectedBarcodeSize.value = qzStore.selectedBarcodeSizeConfig && availableBarcodeSizes.value.some(s => s.value === qzStore.selectedBarcodeSizeConfig.value)
        ? qzStore.selectedBarcodeSizeConfig : availableBarcodeSizes.value[0] ?? null;
      selectedBarcodeTray.value = qzStore.selectedBarcodeTrayConfig && availableBarcodeTrays.value.some(t => t.value === qzStore.selectedBarcodeTrayConfig.value)
        ? qzStore.selectedBarcodeTrayConfig : availableBarcodeTrays.value[0] ?? null;
      // Auto-detect Zebra
      const name = newPrinter.value.toLowerCase();
      if (/zebra|zpl|zd[0-9]|zt[0-9]|gk[0-9]|gx[0-9]/i.test(name)) {
        isZebraPrinterChecked.value = true;
        qzStore.isZebraPrinter = true;
        zebraAutoDetected.value = true;
      } else {
        zebraAutoDetected.value = false;
      }
    } catch {
      availableBarcodeSizes.value = [];
      availableBarcodeTrays.value = [];
    } finally {
      isLoadingBarcodePrinterSizes.value = false;
    }
  },
  { immediate: true }
);

watch([selectedSize, selectedTray], ([s, t]) => qzStore.setSelectedPrinterSizeWidthHeightandTray(s, t));
watch([selectedBarcodeSize, selectedBarcodeTray], ([s, t]) => qzStore.setSelectedBarcodePrinterSizeWidthHeightandTray(s, t));

const checkQzConnection = () => {
  if (typeof qz !== 'undefined') {
    const isNowConnected = qz.websocket.isActive();
    if (qzStore.isQzConnected !== isNowConnected) qzStore.isQzConnected = isNowConnected;
  }
};

onMounted(() => {
  qzStore.initPrinterMode();
  qzStore.initQzConnectionStatus();
  qzStore.initPrinterSettings();
  qzStore.initBarcodePrinterSettings();
  qzStore.initSelectedBarcodePrinter();
  qzStore.initZebraPrinterSetting();
  isZebraPrinterChecked.value = qzStore.isZebraPrinter;
  qzStore.initPrintWithoutPrice();
  isPrintWithoutPriceChecked.value = qzStore.printWithoutPrice;

  setTimeout(() => {
    const userWantsQz = localStorage.getItem('useQzTrayPrint') === 'true';
    if (qzStore.isQzConnected && userWantsQz) {
      qzStore.useQzTrayPrint = true;
      checked.value = true;
    } else if (qzStore.isQzConnected && !userWantsQz) {
      qzStore.disconnectQZ();
      qzStore.useQzTrayPrint = false;
      checked.value = false;
    } else if (!qzStore.isQzConnected && userWantsQz && !qzStore.isConnecting) {
      qzStore.connectQZ(true).then(() => {
        if (qzStore.isQzConnected) {
          qzStore.useQzTrayPrint = true;
          checked.value = true;
        }
      });
    } else {
      qzStore.useQzTrayPrint = false;
      checked.value = false;
    }
  }, 100);

  const interval = setInterval(checkQzConnection, 5000);
  onUnmounted(() => clearInterval(interval));
});
</script>

<template>
  <div v-if="visible" class="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
    <div class="bg-white rounded-lg p-6 max-w-md w-full shadow-xl max-h-[80vh] overflow-y-auto">
      <div class="flex justify-between items-center mb-4">
        <h3 class="text-lg font-semibold">Print Settings</h3>
        <button @click="close" class="text-gray-500 hover:text-gray-700">
          <i class="pi pi-times"></i>
        </button>
      </div>

      <!-- QZ Enable toggle -->
      <div class="card flex flex-row justify-center items-center gap-4">
        <span>Use QZ Tray Print</span>
        <div class="flex flex-col items-center gap-2">
          <Checkbox @change="togglePrinterMode" v-model="checked" :invalid="!checked"
            :disabled="qzStore.isConnecting"
            :class="['w-full sm:w-40 custom-checkbox', qzStore.isQzConnected ? 'checkbox-connected' : 'checkbox-disconnected']"
            binary />
          <div v-if="qzStore.isConnecting" class="text-xs text-yellow-500 text-center">
            <i class="pi pi-spinner pi-spin mr-1"></i>Connecting to QZ Tray...
          </div>
        </div>
      </div>

      <button @click="showPrinterConfig = true" :disabled="!qzStore.useQzTrayPrint"
        class="px-4 py-2 mt-2 bg-purple-500 text-white rounded hover:bg-purple-600 transition disabled:opacity-50 disabled:cursor-not-allowed">
        Printer Settings
      </button>

      <!-- QZ Setup Link -->
      <div class="mt-4">
        <a href="/qz-certificate-import.html" target="_blank" rel="noopener"
          class="inline-flex items-center gap-2 text-purple-600 hover:text-purple-700 text-sm">
          <i class="pi pi-external-link"></i>
          How to turn on one-click fast printing.
        </a>
      </div>
    </div>
  </div>

  <!-- Printer Config slide panel -->
  <transition name="slide-right">
    <div v-if="showPrinterConfig && qzStore.useQzTrayPrint"
      class="fixed inset-0 flex justify-end z-[60] bg-opacity-50">
      <div class="bg-white h-full w-1/4 px-6 py-4 shadow-2xl relative overflow-y-auto">
        <button @click="showPrinterConfig = false"
          class="absolute top-2 right-2 text-gray-500 hover:text-gray-700">
          <i class="pi pi-times"></i>
        </button>
        <h3 class="text-lg font-semibold mb-4">Printer Configuration</h3>

        <Accordion :value="['1', '2']" multiple>
          <!-- Receipt Printer -->
          <AccordionPanel value="1">
            <AccordionHeader>Receipt Printer</AccordionHeader>
            <AccordionContent>
              <div class="bg-gray-100 rounded pb-3 px-3">
                <div class="py-2 flex flex-col relative z-50">
                  <Select :disabled="!qzStore.useQzTrayPrint" v-model="qzStore.selectedPrinter"
                    :options="qzStore.allPrinters" optionLabel="label" placeholder="Select Printer" class="font-medium" />
                </div>
                <div class="flex flex-col gap-3">
                  <div class="flex gap-2">
                    <label class="w-28 text-left text-sm text-gray-600">Paper Size</label>
                    <Select v-model="selectedSize" :options="availableSizes" optionLabel="label"
                      placeholder="Select Paper Size" class="flex-1 font-medium" :loading="isLoadingPrinterSizes" />
                  </div>
                  <div class="flex gap-2">
                    <label class="w-28 text-left text-sm text-gray-600">Trays</label>
                    <Select v-model="selectedTray" :options="availableTrays" optionLabel="label"
                      placeholder="Select Tray" class="flex-1 font-medium" />
                  </div>
                </div>
                <div class="flex flex-col mt-4 gap-4">
                  <div class="flex items-start gap-2">
                    <label class="w-28 text-sm text-gray-600 text-left mt-1">Margins (in)</label>
                    <div class="flex flex-col gap-2 flex-1 max-w-[400px]">
                      <input v-model.number="qzStore.selectedMarginConfig.top" type="number" placeholder="Top" class="border px-2 py-2 w-full rounded" />
                      <input v-model.number="qzStore.selectedMarginConfig.right" type="number" placeholder="Right" class="border px-2 py-2 w-full rounded" />
                      <input v-model.number="qzStore.selectedMarginConfig.bottom" type="number" placeholder="Bottom" class="border px-2 py-2 w-full rounded" />
                      <input v-model.number="qzStore.selectedMarginConfig.left" type="number" placeholder="Left" class="border px-2 py-2 w-full rounded" />
                    </div>
                  </div>
                  <div class="flex gap-2">
                    <label class="w-28 text-sm text-gray-600 text-left">Scale (%)</label>
                    <div class="flex-1 max-w-[400px]">
                      <input v-model.number="qzStore.selectedScaleConfig" type="number" min="10" max="200" class="border px-2 py-2 rounded w-full" />
                    </div>
                  </div>
                </div>
              </div>
            </AccordionContent>
          </AccordionPanel>

          <!-- Barcode Printer -->
          <AccordionPanel value="2">
            <AccordionHeader>Barcode Printer</AccordionHeader>
            <AccordionContent>
              <div class="bg-gray-100 rounded pb-3 px-3">
                <div class="py-2 flex flex-col relative z-50">
                  <Select :disabled="!qzStore.useQzTrayPrint" v-model="qzStore.selectedBarcodePrinter"
                    :options="qzStore.allPrinters" optionLabel="label" placeholder="Select Printer" class="font-medium" />
                  <div v-if="qzStore.selectedBarcodePrinter" class="mt-1.5 flex items-center gap-1.5">
                    <span :class="isZebraPrinterChecked ? 'bg-green-100 text-green-700 border-green-200' : 'bg-gray-100 text-gray-500 border-gray-200'"
                      class="text-xs px-2 py-0.5 rounded-full border font-medium">
                      {{ isZebraPrinterChecked ? 'ZPL Mode' : 'HTML Mode' }}
                    </span>
                    <span v-if="zebraAutoDetected" class="text-xs text-gray-400">auto-detected</span>
                  </div>
                </div>

                <div class="flex justify-between items-center pt-1 pb-2">
                  <div>
                    <label class="text-sm text-gray-600">Zebra / ZPL Printer</label>
                    <p class="text-xs text-gray-400">Send raw ZPL commands (Zebra, thermal)</p>
                  </div>
                  <Checkbox @change="toggleZebraPrinter" v-model="isZebraPrinterChecked"
                    :class="['custom-checkbox', qzStore.isZebraPrinter ? 'checkbox-connected' : 'checkbox-disconnected']"
                    binary />
                </div>

                <template v-if="!isZebraPrinterChecked">
                  <div class="flex flex-col gap-3">
                    <div class="flex gap-2">
                      <label class="w-28 text-left text-sm text-gray-600">Paper Size</label>
                      <Select v-model="selectedBarcodeSize" :options="availableBarcodeSizes" optionLabel="label"
                        placeholder="Select Paper Size" class="flex-1 font-medium" :loading="isLoadingBarcodePrinterSizes" />
                    </div>
                    <div class="flex gap-2">
                      <label class="w-28 text-left text-sm text-gray-600">Trays</label>
                      <Select v-model="selectedBarcodeTray" :options="availableBarcodeTrays" optionLabel="label"
                        placeholder="Select Tray" class="flex-1 font-medium" />
                    </div>
                  </div>
                  <div class="flex flex-col mt-4 gap-4">
                    <div class="flex items-start gap-2">
                      <label class="w-28 text-sm text-gray-600 text-left mt-1">Margins (in)</label>
                      <div class="flex flex-col gap-2 flex-1 max-w-[400px]">
                        <input v-model.number="qzStore.selectedBarcodeMarginConfig.top" type="number" placeholder="Top" class="border px-2 py-2 w-full rounded" />
                        <input v-model.number="qzStore.selectedBarcodeMarginConfig.right" type="number" placeholder="Right" class="border px-2 py-2 w-full rounded" />
                        <input v-model.number="qzStore.selectedBarcodeMarginConfig.bottom" type="number" placeholder="Bottom" class="border px-2 py-2 w-full rounded" />
                        <input v-model.number="qzStore.selectedBarcodeMarginConfig.left" type="number" placeholder="Left" class="border px-2 py-2 w-full rounded" />
                      </div>
                    </div>
                    <div class="flex gap-2">
                      <label class="w-28 text-sm text-gray-600 text-left">Scale (%)</label>
                      <div class="flex-1 max-w-[400px]">
                        <input v-model.number="qzStore.selectedBarcodeScaleConfig" type="number" min="10" max="200" class="border px-2 py-2 rounded w-full" />
                      </div>
                    </div>
                  </div>
                </template>

                <div class="flex justify-between items-center pt-3">
                  <label class="text-sm text-gray-600">Include Price</label>
                  <Checkbox @change="togglePrintWithoutPrice" v-model="isPrintWithoutPriceChecked"
                    :class="['w-full sm:w-40 custom-checkbox', qzStore.printWithoutPrice ? 'checkbox-connected' : 'checkbox-disconnected']"
                    binary />
                </div>
              </div>
            </AccordionContent>
          </AccordionPanel>
        </Accordion>
      </div>
    </div>
  </transition>
</template>

<style scoped>
.custom-checkbox :deep(.p-checkbox-box) {
  width: 2rem !important;
  height: 2rem !important;
  border-radius: 0.375rem !important;
}
.custom-checkbox :deep(.p-checkbox-icon) {
  font-size: 1.25rem !important;
}
.checkbox-connected :deep(.p-checkbox-box) {
  background-color: #a855f7 !important;
  border-color: #a855f7 !important;
}
.checkbox-connected :deep(.p-checkbox-icon) {
  color: #ffffff !important;
}
.checkbox-disconnected :deep(.p-checkbox-box) {
  background-color: #ffffff !important;
  border: 1px solid #9ca3af !important;
}
.checkbox-disconnected :deep(.p-checkbox-icon) {
  color: transparent !important;
}

:deep(.p-select) {
  border: 1px solid #d1d5db !important;
  border-radius: 0.5rem !important;
  padding: 0.5rem;
}
:deep(.p-select:not(.p-disabled):hover) { border-color: #9ca3af !important; }
:deep(.p-select:focus-within), :deep(.p-select.p-focus) {
  box-shadow: none !important;
  border-color: #9ca3af !important;
}
:deep(.p-accordionheader) {
  border: none !important;
  padding: 1rem;
  background-color: #f3f4f6 !important;
}
:deep(.p-accordionpanel) {
  border: none !important;
  margin-bottom: 1rem !important;
}
:deep(.p-accordioncontent-content) { border: none !important; }

.slide-right-enter-active, .slide-right-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}
.slide-right-enter-from, .slide-right-leave-to {
  transform: translateX(100%);
  opacity: 0;
}
</style>
